// src/utils/useCommaInput.ts
// ─────────────────────────────────────────────────────────────────────────────
// Shared hook for comma-formatted number inputs.
// On FOCUS: show raw number (so user can edit cleanly).
// On BLUR:  format with commas (e.g. 42885 → 42,885).
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback, FocusEvent, ChangeEvent } from 'react';

const commaFormat = (n: number): string =>
  !n || isNaN(n) ? '' : n.toLocaleString('en-GB');
const parseComma = (s: string): number =>
  parseFloat(s.replace(/,/g, '')) || 0;

/**
 * Hook that manages a comma-formatted number input.
 *
 * @param value   Current numeric value from state
 * @param onChange Callback when value changes (receives parsed number)
 * @returns Props to spread onto an <input type="text"> element
 */
export function useCommaInput(value: number, onChange: (v: number) => void, live?: boolean) {
  live = live ?? false;
  const [editing, setEditing] = useState(false);
  const [rawText, setRawText] = useState('');

  const displayValue = editing ? rawText : commaFormat(value);

  const onFocus = useCallback((_e: FocusEvent<HTMLInputElement>) => {
    setEditing(true);
    setRawText(value ? String(value) : '');
  }, [value]);

  const onBlur = useCallback((_e: FocusEvent<HTMLInputElement>) => {
    const parsed = parseComma(rawText);
    onChange(parsed);
    setEditing(false);
  }, [rawText, onChange]);

  const onChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    // Allow digits, commas, decimal point only
    const cleaned = e.target.value.replace(/[^0-9.,]/g, '');
    setRawText(cleaned);
    if (live) {
      onChange(parseComma(cleaned));
    }
  }, [live, onChange]);

  return {
    type: 'text' as const,
    inputMode: 'numeric' as const,
    value: displayValue,
    onChange: onChangeInput,
    onFocus,
    onBlur,
  };
}
